/**
 * Filename contract for folder-flow: `<row_index>.<custom>.<ext>` OR
 * `<row_index>.<ext>` (middle custom part is optional). row_index is a
 * positive integer, ext is one of the four supported image formats.
 * Mirrors the server-side regex in FolderImageMapperService.
 */
const FILENAME_REGEX = /^([1-9]\d*)(\..+)?\.(jpg|jpeg|png|bmp)$/i;

const SUPPORTED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'bmp']);

const MAX_BYTES = 1024 * 1024;

const MAGIC = {
    png: [0x89, 0x50, 0x4e, 0x47],
    jpegA: [0xff, 0xd8, 0xff],
    bmp: [0x42, 0x4d],
} as const;

export function parseRowIndex(filename: string): number | null {
    const m = FILENAME_REGEX.exec(filename);
    return m ? parseInt(m[1], 10) : null;
}

export function isImageFilename(filename: string): boolean {
    const lower = filename.toLowerCase();
    const dot = lower.lastIndexOf('.');
    if (dot < 0) return false;
    return SUPPORTED_EXTENSIONS.has(lower.slice(dot + 1));
}

export function isWithinSizeLimit(file: File): boolean {
    return file.size > 0 && file.size <= MAX_BYTES;
}

export async function checkMagicBytes(file: File): Promise<boolean> {
    const slice = file.slice(0, 8);
    const buf = await slice.arrayBuffer();
    const b = new Uint8Array(buf);

    return startsWith(b, MAGIC.png) || startsWith(b, MAGIC.jpegA) || startsWith(b, MAGIC.bmp);
}

export async function isImageDecodable(file: File): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(true);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(false);
        };
        img.src = url;
    });
}

function startsWith(haystack: Uint8Array, prefix: ReadonlyArray<number>): boolean {
    if (haystack.length < prefix.length) return false;
    for (let i = 0; i < prefix.length; i++) {
        if (haystack[i] !== prefix[i]) return false;
    }
    return true;
}

export const FOLDER_FILE_LIMITS = {
    maxBytesPerFile: MAX_BYTES,
    maxFilesPerBatch: 200,
};
